import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft, CheckCircle, ExternalLink } from "lucide-react";

export const Step = ({ n, title, children }) => (
  <div className="flex gap-6 mb-12" data-testid={`step-${n}`}>
    <div className="flex-shrink-0 w-12 h-12 border border-[#D95B38] bg-[#D95B38]/10 flex items-center justify-center font-serif text-xl text-[#D95B38]">
      {n}
    </div>
    <div className="flex-1 min-w-0 pt-1">
      <h3 className="font-serif text-2xl mb-3">{title}</h3>
      <div className="font-mono text-sm text-[#E0D8CA] leading-relaxed space-y-3">{children}</div>
    </div>
  </div>
);

export const Tip = ({ children }) => (
  <div className="bg-[#1A1715] border-l-2 border-[#D95B38] px-5 py-3 text-[#A39A8F] font-mono text-xs">
    <span className="text-[#D95B38] uppercase tracking-[0.25em] mr-2">Tip</span>
    {children}
  </div>
);

export const CodeBlock = ({ children }) => (
  <pre className="bg-[#0F0D0C] border border-[#F5F2EB]/10 p-4 font-mono text-xs text-[#E0D8CA] overflow-x-auto whitespace-pre-wrap break-all">
    {children}
  </pre>
);

export const Check = ({ children }) => (
  <li className="flex gap-2 items-start"><CheckCircle className="w-4 h-4 inline text-[#D95B38] mt-0.5 shrink-0" />{children}</li>
);

export const Ext = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#D95B38] hover:underline inline-flex items-center gap-1">
    {children}<ExternalLink className="w-3 h-3" />
  </a>
);

export default function ArticleLayout({ kicker, title, intro, children, backToHelp = true }) {
  const { user } = useAuth({ redirectIfUnauthed: false });
  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Navbar user={user} />
      <div className="mx-auto max-w-3xl px-6 md:px-12 py-16 md:py-24">
        <Link to={backToHelp ? "/help" : "/dashboard"}
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hover:text-[#F5F2EB] mb-10">
          <ArrowLeft className="w-3 h-3" /> {backToHelp ? "Back to help" : "Back to dashboard"}
        </Link>
        {kicker && <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">{kicker}</div>}
        <h1 className="font-serif text-4xl sm:text-5xl mb-6">{title}</h1>
        {intro && <p className="font-mono text-sm text-[#A39A8F] mb-12 leading-relaxed max-w-2xl">{intro}</p>}
        {children}
      </div>
      <Footer />
    </div>
  );
}
